import { useId } from 'react';
import { classNames } from './class-names.js';

export interface ChoiceOption {
  value: string;
  label: string;
  hint?: string;
  disabled?: boolean;
  badge?: string;
}

interface ChoiceGroupBaseProps {
  legend: string;
  name: string;
  options: ChoiceOption[];
  error?: string;
  hint?: string;
}

export interface RadioGroupProps extends ChoiceGroupBaseProps {
  value: string;
  onChange: (value: string) => void;
}

/**
 * Card-style choices over a native radio, so keyboard navigation, focus rings and screen-reader
 * semantics come for free rather than being reimplemented.
 */
export function RadioGroup({ legend, name, options, value, onChange, error, hint }: RadioGroupProps) {
  const messageId = useId();
  const hasMessage = Boolean(error || hint);

  return (
    <fieldset
      className="fieldset w-full"
      aria-invalid={error ? true : undefined}
      aria-describedby={hasMessage ? messageId : undefined}
      tabIndex={error ? -1 : undefined}
    >
      <legend className="fieldset-legend">{legend}</legend>
      <div className="grid gap-2 sm:grid-cols-2">
        {options.map((option) => (
          <label
            key={option.value}
            className={choiceClass(value === option.value, option.disabled)}
          >
            <input
              type="radio"
              className="radio radio-primary radio-sm"
              name={name}
              value={option.value}
              checked={value === option.value}
              disabled={option.disabled}
              onChange={() => onChange(option.value)}
            />
            <ChoiceBody option={option} />
          </label>
        ))}
      </div>
      <ChoiceMessage id={messageId} error={error} hint={hint} />
    </fieldset>
  );
}

export interface CheckboxGroupProps extends ChoiceGroupBaseProps {
  values: string[];
  onToggle: (value: string, checked: boolean) => void;
}

export function CheckboxGroup({
  legend,
  name,
  options,
  values,
  onToggle,
  error,
  hint,
}: CheckboxGroupProps) {
  const messageId = useId();
  const hasMessage = Boolean(error || hint);

  return (
    <fieldset
      className="fieldset w-full"
      aria-invalid={error ? true : undefined}
      aria-describedby={hasMessage ? messageId : undefined}
      tabIndex={error ? -1 : undefined}
    >
      <legend className="fieldset-legend">{legend}</legend>
      <div className="grid gap-2 sm:grid-cols-2">
        {options.map((option) => (
          <label
            key={option.value}
            className={choiceClass(values.includes(option.value), option.disabled)}
          >
            <input
              type="checkbox"
              className="checkbox checkbox-primary checkbox-sm"
              name={name}
              value={option.value}
              checked={values.includes(option.value)}
              disabled={option.disabled}
              onChange={(event) => onToggle(option.value, event.target.checked)}
            />
            <ChoiceBody option={option} />
          </label>
        ))}
      </div>
      <ChoiceMessage id={messageId} error={error} hint={hint} />
    </fieldset>
  );
}

function ChoiceBody({ option }: { option: ChoiceOption }) {
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="flex items-center gap-2">
        <span className="min-w-0 break-words font-medium">{option.label}</span>
        <ChoiceBadge badge={option.badge} />
      </span>
      <ChoiceHint hint={option.hint} />
    </span>
  );
}

function ChoiceBadge({ badge }: { badge?: string }) {
  if (!badge) {
    return null;
  }

  return <span className="badge badge-sm badge-ghost">{badge}</span>;
}

function ChoiceHint({ hint }: { hint?: string }) {
  if (!hint) {
    return null;
  }

  return <span className="text-xs opacity-70">{hint}</span>;
}

function ChoiceMessage({ id, error, hint }: { id: string; error?: string; hint?: string }) {
  if (error) {
    return (
      <p id={id} className="label text-error">
        {error}
      </p>
    );
  }

  if (!hint) {
    return null;
  }

  return (
    <p id={id} className="label">
      {hint}
    </p>
  );
}

function choiceClass(selected: boolean, disabled?: boolean): string {
  return classNames(
    'flex min-w-0 cursor-pointer items-start gap-3 rounded-field border p-3 transition-colors',
    selected && 'border-primary bg-primary/5',
    !selected && 'border-base-300 hover:border-primary/40',
    disabled && 'cursor-not-allowed opacity-50 hover:border-base-300',
  );
}
